import { useSearchParams, Link } from 'react-router-dom';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Toolbar from '@mui/material/Toolbar';
import Stack from '@mui/material/Stack';
import * as Device from 'react-device-detect';

import { useSession } from '@hooks';
import { GetConversationInfosQuery } from '@graphql';
import { getConversationTitle } from '@utils';

interface HeaderProps {
  handleCloseDialog: () => void;
  conversation: GetConversationInfosQuery['conversation'];
}

export function Header(props: Readonly<HeaderProps>) {
  const { handleCloseDialog, conversation } = props;
  const theme = useTheme();
  const { user, account } = useSession();
  const [_searchParams, setSearchParams] = useSearchParams();

  const avatarSize = 40;

  if (!user || !account || !conversation) {
    return null;
  }

  const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
    const participant = conversation.members.find((m) => m.id !== account.id);

    if (!participant) {
      return;
    }

    const [key] = participant.__typename.toLowerCase();

    e.preventDefault();
    setSearchParams(
      {
        [key]: participant.slug,
        tab: 'preview',
      },
      { preventScrollReset: true }
    );
  };

  return (
    <AppBar position={Device.isMobile ? 'fixed' : 'static'} elevation={0}>
      <Toolbar sx={{ width: '100%' }}>
        <Stack direction="row" spacing={1} alignItems="center" width="100%">
          {conversation.members.length > 2 ? (
            <theme.icons.people sx={{ width: avatarSize, height: avatarSize }} />
          ) : (
            <Avatar
              src={conversation.members.find((m) => m.id !== account.id)?.avatar?.url || null}
              sx={{ width: avatarSize, height: avatarSize }}
            />
          )}
          <Stack spacing={0} alignItems="center" width="100%">
            <Typography variant="h6" fontWeight="bold" width="100%">
              {getConversationTitle(account.id, conversation)}
            </Typography>
            {conversation.members.length === 2 ? (
              <Duo members={conversation.members} account={account} handleClick={handleOnClick} />
            ) : (
              <Group members={conversation.members} account={account} handleClick={handleOnClick} />
            )}
          </Stack>
          <IconButton onClick={handleCloseDialog}>
            <theme.icons.close color="inherit" />
          </IconButton>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

interface ParticipantProps {
  members: GetConversationInfosQuery['conversation']['members'];
  account: NonNullable<ReturnType<typeof useSession>['account']>;
  handleClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const Duo = (props: ParticipantProps) => {
  const { members, account, handleClick } = props;

  return (
    <Typography
      component={Link}
      to={`?type=spot&q=${account.slug}`}
      onClick={handleClick}
      variant="body2"
      color="text.secondary"
      width="100%"
      sx={{
        alignSelf: 'flex-start',
        width: 'auto',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover': { color: 'white' },
      }}
    >
      {'@' + members.find((m) => m.id !== account.id)?.slug}
    </Typography>
  );
};

const Group = (props: ParticipantProps) => (
  <Typography variant="body2" color="text.secondary" width="100%">
    {props.members
      .filter((m) => m.id !== props.account.id)
      .map((m) => m.name)
      .join(', ')}
  </Typography>
);
